import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { useAppSelector } from "../../../Config/Hooks";
import TotalizerAlarmsPlantSkid from "./TotalizerAlarmsPlantSkid/TotalizerAlarmsPlantSkid";
import AlarmPage from "./Alarms/AlarmPage";
import React, { useState } from "react";
import { TotalizerType } from "../Overview/DashboardOverviewAlarmsPage";
import { ASectionTitle, ADataGridFilter } from "@atiautomacao/ati-ui-library";
import { LoadingButton } from "@mui/lab";
import SideBar from "../Alarms/Components/sideBarFilterAlarms";
import { ChipFilterAlarms } from "../Alarms/Components/ChipFilterAlarms";
import { Storage } from "../../../Utils/StorageUtils";
import { ALARM_ADVANCED_FILTERS_KEY } from "../Alarms/Components/ChipFilterAlarms/types";

const ALARM_FILTERS_COUNT_KEY = 'alarm_filters_count';
const TOTALIZER_ALARM_FILTER_KEY = 'totalizer_alarm_filter';

const getInitialFiltersCount = (): number => {
    const stored = Number(Storage.local.get(ALARM_FILTERS_COUNT_KEY, 0));
    return Number.isFinite(stored) && stored >= 0 ? stored : 0;
};

const computeInitialAdvancedFilters = (
    skidId: number | undefined,
    totalizer: TotalizerType
): ADataGridFilter[] => {
    const stored = Storage.local.get(ALARM_ADVANCED_FILTERS_KEY, null);
    const base: ADataGridFilter[] = (stored && Array.isArray(stored))
        ? (stored as ADataGridFilter[]).filter(f => f.name !== 'skidId' && f.name !== 'totalizerType')
        : [];
    if (!skidId) return base;
    base.push({ name: 'skidId', condition: 'equals', value: skidId.toString(), sort: 'none' });
    if (totalizer !== 'none') {
        base.push({ name: 'totalizerType', condition: 'equals', value: totalizer, sort: 'none' });
    }
    return base;
};

export default function DashboardPowerPlantSkidAlarmsPage() {
    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));

    const { selectedPowerPlant, selectedSkid } = useAppSelector((state) => state.dashboard);
    const [sideBarOpen, setSideBarOpen] = useState(false);
    const [totalizerFilter, setTotalizerFilter] = useState<TotalizerType>(localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none");
    const [advancedFilters, setAdvancedFilters] = useState<ADataGridFilter[]>(
        () => computeInitialAdvancedFilters(selectedSkid?.id, localStorage.getItem(TOTALIZER_ALARM_FILTER_KEY) as TotalizerType ?? "none")
    );
    const [isClearFilters, setIsClearFilters] = useState<boolean>(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState<number>(getInitialFiltersCount);

    const mergeFiltersWithTotalizerAndSkid = (
        sidebarFilters: ADataGridFilter[],
        totalizer: TotalizerType,
        skidId: number
    ): ADataGridFilter[] => {
        // Remove filtros anteriores de totalizer e skidId para evitar duplicatas
        const baseFilters = sidebarFilters.filter(
            f => f.name !== 'totalizerType' && f.name !== 'skidId'
        );

        baseFilters.push({
            name: 'skidId',
            condition: 'equals',
            value: skidId.toString(),
            sort: 'none'
        });

        if (totalizer !== 'none') {
            baseFilters.push({
                name: 'totalizerType',
                condition: 'equals',
                value: totalizer,
                sort: 'none'
            });
        }

        return baseFilters;
    };

    const handleTotalizerChange = (totalizer: TotalizerType) => {
        if (!selectedSkid) return;
        localStorage.setItem(TOTALIZER_ALARM_FILTER_KEY, totalizer);
        setTotalizerFilter(totalizer);
        setAdvancedFilters(prev =>
            mergeFiltersWithTotalizerAndSkid(prev, totalizer, selectedSkid.id)
        );
        setIsClearFilters(false);
    };

    const handleApplySideBarFilters = (sidebarFilters: ADataGridFilter[], filtersCount: number) => {
        if (!selectedSkid) return;

        const merged = mergeFiltersWithTotalizerAndSkid(sidebarFilters, totalizerFilter, selectedSkid.id);
        setAdvancedFilters(merged);
        Storage.local.set(ALARM_ADVANCED_FILTERS_KEY, merged.filter(f => f.name !== 'skidId' && f.name !== 'totalizerType'));
        setSideBarOpen(false);
        setAppliedFiltersCount(filtersCount);
        Storage.local.set(ALARM_FILTERS_COUNT_KEY, filtersCount);
        setIsClearFilters(false);
    };

    const handleClearFilters = (cleared: boolean) => {
        if (cleared) {
            setAdvancedFilters([]);
            setAppliedFiltersCount(0);
            Storage.local.remove(ALARM_ADVANCED_FILTERS_KEY);
            Storage.local.remove(ALARM_FILTERS_COUNT_KEY);
            setIsClearFilters(cleared);
            Storage.local.remove(TOTALIZER_ALARM_FILTER_KEY);
        }
    };

    return (
        <Grid container paddingLeft={'24px'} paddingRight={'24px'} paddingBottom={'24px'}>
            <Grid item xs={12} paddingTop={0}>
                <ASectionTitle
                    titleLabel={"Alarmes"}
                    styleContainer={{ padding: '0px' }}
                    styleContainerTitle={{ padding: '0px' }}
                    contentChildrenStyle={{ display: isXl ? 'none' : 'flex' }}
                    contentChildren={
                        <LoadingButton
                            size="small"
                            loading={false}
                            startIcon={
                                <Box
                                    sx={{
                                        color: theme.palette.primary.main,
                                        backgroundColor: '#fff',
                                        fontSize: '0.75rem !important',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {appliedFiltersCount}
                                </Box>
                            }
                            variant="contained"
                            onClick={() => setSideBarOpen(true)}
                        >
                            Filtros Avançados
                        </LoadingButton>
                    }
                />
            </Grid>
            <Grid item xs={12} paddingTop={'16px'}>
                {selectedPowerPlant && selectedSkid &&
                    <TotalizerAlarmsPlantSkid
                        skidId={selectedSkid.id}
                        onClickTotalizer={handleTotalizerChange}
                        clearFilters={isClearFilters}
                    />
                }
            </Grid>
            <Grid item xs={12} paddingTop={'4px'} sx={{ display: isXl ? 'block' : 'none' }}>
                <ChipFilterAlarms onApplyFilters={handleApplySideBarFilters} hasClearFilters={isClearFilters} />
            </Grid>
            <Grid item xs={12} paddingTop={'24px'}>
                {selectedSkid ?
                    <AlarmPage
                        skidId={selectedSkid.id}
                        advancedFilters={advancedFilters}
                        hasClearFilters={handleClearFilters}
                    />
                    : null
                }
            </Grid>
            <SideBar
                open={sideBarOpen}
                onClose={() => setSideBarOpen(false)}
                onApplyFilters={handleApplySideBarFilters}
                hasClearFilters={isClearFilters}
            />
        </Grid>
    );
}
